import type { UnifiedPayload, LogEntry, MetricEntry } from '../core/MonitoringClient'
import { MetricFormatter } from './MetricFormatter'
import { LogFormatter } from './LogFormatter'

export interface ProcessedPayload {
  payload: string
  size: number
  compressed: boolean
  chunks?: string[]
}

export interface CompressionOptions {
  removeWhitespace: boolean
  abbreviateKeys: boolean
  maxStringLength: number
}

export class PayloadProcessor {
  static create(
    metricFormatter?: MetricFormatter,
    logFormatter?: LogFormatter,
    compressionOptions?: Partial<CompressionOptions>
  ): PayloadProcessor {
    return new PayloadProcessor(
      metricFormatter ?? MetricFormatter.create(),
      logFormatter ?? LogFormatter.create(),
      compressionOptions
    )
  }

  private readonly compressionOptions: CompressionOptions

  constructor(
    private readonly metricFormatter: MetricFormatter,
    private readonly logFormatter: LogFormatter,
    compressionOptions?: Partial<CompressionOptions>
  ) {
    this.compressionOptions = {
      removeWhitespace: compressionOptions?.removeWhitespace ?? true,
      abbreviateKeys: compressionOptions?.abbreviateKeys ?? true,
      maxStringLength: compressionOptions?.maxStringLength ?? 200,
    }
  }

  /**
   * Compresses a unified payload to reduce size
   */
  compress(payload: UnifiedPayload, options: Partial<CompressionOptions> = {}): string {
    const opts = { ...this.compressionOptions, ...options }

    let compressed = payload

    // Apply compression techniques
    if (opts.abbreviateKeys) {
      compressed = this.abbreviateKeys(compressed)
    }

    if (opts.maxStringLength > 0) {
      compressed = this.truncateStrings(compressed, opts.maxStringLength)
    }

    // Serialize with optional whitespace removal
    const jsonString = opts.removeWhitespace
      ? JSON.stringify(compressed)
      : JSON.stringify(compressed, null, 2)

    return jsonString
  }

  /**
   * Chunks a payload into smaller pieces if it exceeds maxSize
   */
  chunk(payload: string, maxSize: number): string[] {
    if (payload.length <= maxSize) {
      return [payload]
    }

    const chunks: string[] = []

    try {
      const parsed = JSON.parse(payload) as UnifiedPayload

      // Try to split by logs and metrics first
      if (parsed.logs && parsed.metrics) {
        const logsPayload = JSON.stringify({ logs: parsed.logs })
        const metricsPayload = JSON.stringify({ metrics: parsed.metrics })

        if (logsPayload.length <= maxSize && metricsPayload.length <= maxSize) {
          return [logsPayload, metricsPayload]
        }
      }

      // If that doesn't work, chunk logs and metrics separately
      if (parsed.logs) {
        chunks.push(...this.chunkLogs(parsed.logs, maxSize))
      }

      if (parsed.metrics) {
        chunks.push(...this.chunkMetrics(parsed.metrics, maxSize))
      }

      return chunks.length > 0 ? chunks : [payload.substring(0, maxSize)]
    } catch {
      // If parsing fails, do simple string chunking
      return this.chunkString(payload, maxSize)
    }
  }

  /**
   * Optimizes a payload by removing redundancy and compressing data
   */
  optimize(payload: UnifiedPayload): UnifiedPayload {
    const optimized: UnifiedPayload = {}

    if (payload.logs && payload.logs.length > 0) {
      optimized.logs = this.optimizeLogs(payload.logs)
    }

    if (payload.metrics && payload.metrics.length > 0) {
      optimized.metrics = this.optimizeMetrics(payload.metrics)
    }

    return optimized
  }

  /**
   * Processes a payload completely: optimize, compress, and chunk if needed
   */
  process(
    payload: UnifiedPayload,
    maxSize: number,
    compressionOptions?: Partial<CompressionOptions>
  ): ProcessedPayload {
    // Step 1: Optimize the payload
    const optimized = this.optimize(payload)

    // Step 2: Compress the payload
    const compressed = this.compress(optimized, compressionOptions)

    // Step 3: Check if chunking is needed
    if (compressed.length <= maxSize) {
      return {
        payload: compressed,
        size: compressed.length,
        compressed: true,
      }
    }

    // Step 4: Chunk if necessary
    const chunks = this.chunk(compressed, maxSize)

    return {
      payload: chunks[0], // Return first chunk as main payload
      size: compressed.length,
      compressed: true,
      chunks: chunks.slice(1), // Additional chunks
    }
  }

  /**
   * Abbreviates common keys to reduce payload size
   */
  private abbreviateKeys(payload: UnifiedPayload): UnifiedPayload {
    const keyMap: Record<string, string> = {
      timestamp: 'ts',
      message: 'msg',
      metadata: 'meta',
      sessionId: 'sid',
      userAgent: 'ua',
      usecase: 'uc',
      duration: 'dur',
      success: 'ok',
    }

    return JSON.parse(
      JSON.stringify(payload, (key, value) => {
        return value
      }).replace(
        /"(timestamp|message|metadata|sessionId|userAgent|usecase|duration|success)":/g,
        (match, key) => `"${keyMap[key] || key}":`
      )
    )
  }

  /**
   * Truncates long strings in the payload
   */
  private truncateStrings(payload: UnifiedPayload, maxLength: number): UnifiedPayload {
    return JSON.parse(
      JSON.stringify(payload, (key, value) => {
        if (typeof value === 'string' && value.length > maxLength) {
          return this.logFormatter.truncateMessage(value, maxLength)
        }
        return value
      })
    )
  }

  /**
   * Chunks logs into smaller arrays
   */
  private chunkLogs(logs: LogEntry[], maxSize: number): string[] {
    const chunks: string[] = []
    let currentChunk: LogEntry[] = []
    let currentSize = 0

    logs.forEach(log => {
      const logSize = JSON.stringify({ logs: [log] }).length

      if (currentSize + logSize > maxSize && currentChunk.length > 0) {
        chunks.push(JSON.stringify({ logs: currentChunk }))
        currentChunk = [log]
        currentSize = logSize
      } else {
        currentChunk.push(log)
        currentSize += logSize
      }
    })

    if (currentChunk.length > 0) {
      chunks.push(JSON.stringify({ logs: currentChunk }))
    }

    return chunks
  }

  /**
   * Chunks metrics into smaller arrays
   */
  private chunkMetrics(metrics: MetricEntry[], maxSize: number): string[] {
    const chunks: string[] = []
    let currentChunk: MetricEntry[] = []
    let currentSize = 0

    metrics.forEach(metric => {
      const metricSize = JSON.stringify({ metrics: [metric] }).length

      if (currentSize + metricSize > maxSize && currentChunk.length > 0) {
        chunks.push(JSON.stringify({ metrics: currentChunk }))
        currentChunk = [metric]
        currentSize = metricSize
      } else {
        currentChunk.push(metric)
        currentSize += metricSize
      }
    })

    if (currentChunk.length > 0) {
      chunks.push(JSON.stringify({ metrics: currentChunk }))
    }

    return chunks
  }

  /**
   * Simple string chunking as fallback
   */
  private chunkString(str: string, maxSize: number): string[] {
    const chunks: string[] = []
    for (let i = 0; i < str.length; i += maxSize) {
      chunks.push(str.substring(i, i + maxSize))
    }
    return chunks
  }

  /**
   * Optimizes logs by removing duplicates and redundant data
   */
  private optimizeLogs(logs: LogEntry[]): LogEntry[] {
    // Remove exact duplicates
    const seen = new Set<string>()
    const uniqueLogs: LogEntry[] = []

    logs.forEach(log => {
      // Create a hash of the log (excluding timestamp for dedup)
      const logHash = this.logFormatter.serializeSafe({
        level: log.level,
        message: log.message,
        metadata: log.metadata,
      })

      if (!seen.has(logHash)) {
        seen.add(logHash)
        uniqueLogs.push(log)
      }
    })

    // Sort by timestamp for better compression
    return uniqueLogs.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0))
  }

  /**
   * Optimizes metrics by removing duplicates and aggregating similar ones
   */
  private optimizeMetrics(metrics: MetricEntry[]): MetricEntry[] {
    // Group by usecase for potential aggregation
    const grouped = new Map<string, MetricEntry[]>()

    metrics.forEach(metric => {
      const key = metric.usecase
      if (!grouped.has(key)) {
        grouped.set(key, [])
      }
      const group = grouped.get(key)
      if (group) {
        group.push(metric)
      }
    })

    const optimized: MetricEntry[] = []

    // For now, just remove exact duplicates and sort
    // In the future, we could implement aggregation here
    Array.from(grouped.values()).forEach(usecaseMetrics => {
      const seen = new Set<string>()

      usecaseMetrics.forEach(metric => {
        const metricHash = this.logFormatter.serializeSafe({
          id: metric.id,
          usecase: metric.usecase,
          duration: metric.duration,
          success: metric.success,
          metadata: metric.metadata,
        })

        if (!seen.has(metricHash)) {
          seen.add(metricHash)
          optimized.push(metric)
        }
      })
    })

    return optimized.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0))
  }

  /**
   * Expands abbreviated keys back to their original form for a single LogEntry
   */
  expandLogKeys(log: LogEntry): LogEntry {
    const reverseKeyMap: Record<string, string> = {
      ts: 'timestamp',
      msg: 'message',
      meta: 'metadata',
      sid: 'sessionId',
      ua: 'userAgent',
      uc: 'usecase',
      dur: 'duration',
      ok: 'success',
    }

    return JSON.parse(
      JSON.stringify(log).replace(
        /"(ts|msg|meta|sid|ua|uc|dur|ok)":/g,
        (match, key) => `"${reverseKeyMap[key] || key}":`
      )
    )
  }
}
